// The titlebar's brand-mark resolver (LG4): read the entered World's brand
// fold, resolve the member to its short-lived signed URL, and re-resolve
// before the URL expires. Unset, loading, and every refusal answer null —
// WorldMark then wears the estate wordmark, the honest fallback.
import { useEffect, useState } from "react";

import {
  isBrandLogoMemberId,
  readWorldBrand,
  resolveBrandMedia,
  WORLD_BRAND_CHANGED_EVENT,
} from "./worldBrandClient";

import { useTheme } from "../hooks/useTheme";

const RERESOLVE_MARGIN_MS = 30_000;

export function useWorldBrandMark(worldKey: string | null): string | null {
  const { resolvedTheme } = useTheme();
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const changed = (event: Event) => {
      if ((event as CustomEvent<string>).detail === worldKey) setRevision((r) => r + 1);
    };
    window.addEventListener(WORLD_BRAND_CHANGED_EVENT, changed);
    return () => window.removeEventListener(WORLD_BRAND_CHANGED_EVENT, changed);
  }, [worldKey]);
  const [mark, setMark] = useState<{ worldKey: string; appearance: string; url: string } | null>(
    null,
  );
  useEffect(() => {
    if (worldKey === null) {
      setMark(null);
      return;
    }
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const resolveMark = async () => {
      const brand = await readWorldBrand(worldKey);
      if (disposed) return;
      const memberId = brand.ok ? (brand[resolvedTheme].memberId ?? brand.memberId) : null;
      if (memberId === null || !isBrandLogoMemberId(memberId)) {
        setMark(null);
        return;
      }
      const media = await resolveBrandMedia(worldKey, memberId);
      if (disposed) return;
      if (!media.ok) {
        setMark(null);
        return;
      }
      setMark({ worldKey, appearance: resolvedTheme, url: media.url });
      const delay = Math.max(media.expiresAt - Date.now() - RERESOLVE_MARGIN_MS, 15_000);
      timer = setTimeout(() => void resolveMark(), delay);
    };
    setMark(null);
    void resolveMark();
    return () => {
      disposed = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [worldKey, resolvedTheme, revision]);
  return mark?.worldKey === worldKey && mark.appearance === resolvedTheme ? mark.url : null;
}
